/**
 * useWlInternalTenantIdForHeader — Hook usado exclusivamente pelo header do
 * TenantLayout para decidir se o botão "Painel WL" deve aparecer.
 *
 * Diferente de `useWlInternalTenant` (que requer `wl.settings:view` para
 * mostrar a tela de Tenant Interno), este hook consulta a RPC dedicada
 * `wl_get_internal_tenant_id_for_header`, cujo gate é `wl.dashboard:view`.
 *
 * Assim, qualquer operador WL com permissão de visualizar o Dashboard do
 * painel WL enxerga o botão de acesso rápido — sem quebrar a segurança da
 * tela de Configurações → Tenant Interno.
 *
 * Em caso de erro (ex: usuário sem `wl.dashboard:view`), o hook devolve
 * `null` silenciosamente para não quebrar a UI.
 */
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { createLogger } from '@/lib/logger';

const logger = createLogger('useWlInternalTenantIdForHeader');

interface Options {
  enabled?: boolean;
}

export function useWlInternalTenantIdForHeader(options?: Options) {
  const { user } = useAuth();
  const enabled = (options?.enabled ?? true) && !!user?.id;

  const query = useQuery({
    queryKey: ['wl-internal-tenant-id-header', user?.id],
    queryFn: async (): Promise<string | null> => {
      const { data, error } = await supabase.rpc(
        'wl_get_internal_tenant_id_for_header' as any,
      );
      if (error) {
        // Silenciar: usuário WL sem `wl.dashboard:view` cai aqui.
        // Não propagar erro para não poluir UI/console do tenant.
        logger.debug('wl_get_internal_tenant_id_for_header negado/erro', {
          code: (error as any)?.code,
          message: (error as any)?.message,
        });
        return null;
      }
      const row = Array.isArray(data) ? data[0] : data;
      return (row?.internal_tenant_id as string | undefined) ?? null;
    },
    enabled,
    staleTime: 5 * 60_000,
    gcTime: 10 * 60_000,
    retry: false,
  });

  return {
    internalTenantId: query.data ?? null,
    isResolved: query.isSuccess || query.isError,
  };
}
